import { useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import { useTranslation } from 'react-i18next'

import { galleryApi } from '../../api/endpoints'
import { PageMeta } from '../../components/common/PageMeta'
import { EmptyState, ErrorState } from '../../components/common/States'
import { Lightbox } from '../../components/common/Lightbox'
import type { GalleryItem, MediaCategory } from '../../types/api'

/** One screenful. The server caps what it will hand out; this is what we ask for. */
const PAGE_SIZE = 15

/**
 * The order the filter is drawn in.
 *
 * Fixed rather than sorted by how many pictures each subject has: the tabs would then
 * reshuffle every time the owner uploaded a batch, and a filter that moves under the thumb is
 * a bad filter. Subjects with nothing behind them are dropped, not greyed out.
 */
const SUBJECT_ORDER: MediaCategory[] = [
  'NATURE',
  'HOUSE',
  'INTERIOR',
  'SAUNA',
  'FOOD',
  'ACTIVITY',
  'DESTINATION',
]

/**
 * Everything the owner has photographed, fifteen at a time.
 *
 * <p>Several hundred pictures exist and until now a visitor could see none of them. Loading
 * them all would be several hundred requests to S3 on a phone, so the page asks for fifteen
 * and offers fifteen more; the button says how many are left, because "load more" with no
 * number tells a visitor nothing about whether it is worth pressing.
 *
 * <p>Clicking one opens it full size with the arrow keys working, which is the first thing
 * everybody tries.
 */
export function GalleryPage() {
  const { t } = useTranslation()
  const [subject, setSubject] = useState<MediaCategory | 'ALL' | 'FILMS'>('ALL')
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const subjects = useQuery({
    queryKey: ['gallery', 'subjects'],
    queryFn: galleryApi.subjects,
  })

  const films = useQuery({ queryKey: ['gallery', 'films'], queryFn: galleryApi.films })

  const pages = useInfiniteQuery({
    queryKey: ['gallery', subject],
    queryFn: ({ pageParam }) =>
      galleryApi.page(
        subject === 'ALL' || subject === 'FILMS' ? undefined : subject,
        pageParam,
        PAGE_SIZE,
        subject === 'FILMS' ? 'VIDEO' : undefined,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  })

  const items: GalleryItem[] = useMemo(
    () => (pages.data?.pages ?? []).flatMap((one) => one.items),
    [pages.data],
  )

  const total = pages.data?.pages[0]?.total ?? 0
  const remaining = Math.max(total - items.length, 0)

  const tabs = useMemo(() => {
    const counts = new Map((subjects.data ?? []).map((one) => [one.category, one.count]))
    return SUBJECT_ORDER.filter((category) => (counts.get(category) ?? 0) > 0).map((category) => ({
      category,
      count: counts.get(category) ?? 0,
    }))
  }, [subjects.data])

  const everything = (subjects.data ?? []).reduce((sum, one) => sum + one.count, 0)

  const show = (delta: number) => {
    setOpenIndex((current) => {
      if (current === null) return current
      const next = current + delta
      return next < 0 || next >= items.length ? current : next
    })
  }

  return (
    <Container sx={{ py: { xs: 6, md: 10 } }}>
      <PageMeta title={t('gallery.title')} description={t('gallery.intro')} path="/gallery" />

      <Typography variant="h1" sx={{ mb: 1 }}>
        {t('gallery.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 680 }}>
        {t('gallery.intro')}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          label={t('gallery.everything') + ' (' + everything + ')'}
          color={subject === 'ALL' ? 'primary' : 'default'}
          variant={subject === 'ALL' ? 'filled' : 'outlined'}
          onClick={() => setSubject('ALL')}
        />
        {(films.data ?? 0) > 0 && (
          <Chip
            label={t('gallery.films') + ' (' + films.data + ')'}
            color={subject === 'FILMS' ? 'primary' : 'default'}
            variant={subject === 'FILMS' ? 'filled' : 'outlined'}
            onClick={() => setSubject('FILMS')}
          />
        )}
        {tabs.map((tab) => (
          <Chip
            key={tab.category}
            label={t('gallery.subject' + tab.category) + ' (' + tab.count + ')'}
            color={subject === tab.category ? 'primary' : 'default'}
            variant={subject === tab.category ? 'filled' : 'outlined'}
            onClick={() => setSubject(tab.category)}
          />
        ))}
      </Stack>

      {pages.isError && <ErrorState error={pages.error} onRetry={() => void pages.refetch()} />}

      {!pages.isError && !pages.isPending && items.length === 0 && (
        <EmptyState icon="📷" title={t('gallery.empty')} body={t('gallery.emptyBody')} />
      )}

      <Box
        sx={{
          display: 'grid',
          // Four across on a laptop rather than three. At three the tiles were 373 px wide
          // and a screenful was five pictures; at four a visitor sees twice as much of the
          // place before reaching for the scroll wheel, which is the point of a gallery.
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {pages.isPending &&
          Array.from({ length: PAGE_SIZE }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={220} />
          ))}

        {items.map((item, index) => (
          <Box
            key={item.url + index}
            onClick={() => setOpenIndex(index)}
            sx={{
              position: 'relative',
              aspectRatio: '4 / 3',
              overflow: 'hidden',
              borderRadius: 3,
              cursor: 'zoom-in',
              bgcolor: 'action.hover',
              '&:hover img': { transform: 'scale(1.04)' },
            }}
          >
            {item.kind === 'VIDEO' ? (
              <>
                <Box
                  component="video"
                  src={item.url}
                  muted
                  playsInline
                  // These films are 20 to 90 MB. Nothing is fetched until somebody opens one:
                  // six autoloading videos in a grid is half a gigabyte on a phone.
                  preload="none"
                  // Without a poster, preload="none" draws a black box. The poster is one frame
                  // of the film, 50-180 KB, made by scripts/make_video_posters.py.
                  poster={item.thumbnailUrl ?? undefined}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', bgcolor: 'grey.900' }}
                />
                <PlayCircleIcon
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 52,
                    color: 'common.white',
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                    pointerEvents: 'none',
                  }}
                />
              </>
            ) : (
              <Box
                component="img"
                // The small copy, falling back to the full size for anything the thumbnailer
                // has not reached. The lightbox always opens the original.
                src={item.thumbnailUrl ?? item.url}
                alt={item.altText ?? ''}
                // Native lazy loading: fifteen at a time is still fifteen requests, and only
                // the first row is on screen when they start.
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 400ms ease',
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      {remaining > 0 && (
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Button
            variant="outlined"
            size="large"
            disabled={pages.isFetchingNextPage}
            onClick={() => void pages.fetchNextPage()}
            startIcon={pages.isFetchingNextPage ? <CircularProgress size={16} /> : undefined}
          >
            {t('gallery.showMore', { count: Math.min(PAGE_SIZE, remaining) })}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('gallery.shownOf', { shown: items.length, total })}
          </Typography>
        </Box>
      )}

      <Lightbox
        items={items}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onMove={show}
      />
    </Container>
  )
}
